const getClientContext = async () => {
  const { userAgentData, platform } = navigator
  if (!userAgentData) return { os: platform || null, osVersion: null, brands: null }

  const brands = userAgentData.brands?.map(({ brand, version }) => ({ brand, version })) ?? null
  const osVersion = await userAgentData
    .getHighEntropyValues(["platformVersion"])
    .then(({ platformVersion }) => platformVersion || null)
    .catch(() => null)

  return { os: userAgentData.platform || null, osVersion, brands }
}

const createExceptionLimiter = ({ bucketSize = 10, windowMs = 10_000 } = {}) => {
  const maxTrackedKeys = 100
  const stateBySignature = new Map()

  const getOrCreateState = (signature) => {
    const existingState = stateBySignature.get(signature)
    if (existingState) return existingState

    const createdState = { remainingBurstInWindow: bucketSize, windowStartedAtMs: Date.now(), nextAllowedAtMs: 0 }
    stateBySignature.set(signature, createdState)
    if (stateBySignature.size <= maxTrackedKeys) return createdState

    const oldestSignature = stateBySignature.keys().next().value
    if (oldestSignature) stateBySignature.delete(oldestSignature)
    return createdState
  }

  const resetBurstWindowIfElapsed = (keyState, nowMs) => {
    if (nowMs - keyState.windowStartedAtMs < windowMs) return
    keyState.windowStartedAtMs = nowMs
    keyState.remainingBurstInWindow = bucketSize
    keyState.nextAllowedAtMs = 0
  }

  return {
    allow(signature) {
      const nowMs = Date.now()
      const keyState = getOrCreateState(signature)
      resetBurstWindowIfElapsed(keyState, nowMs)
      if (keyState.remainingBurstInWindow > 0) {
        keyState.remainingBurstInWindow -= 1
        return true
      }
      if (nowMs < keyState.nextAllowedAtMs) return false
      keyState.nextAllowedAtMs = nowMs + windowMs
      return true
    },
  }
}

function parseStackFrames(stack) {
  const frames = []
  for (const line of String(stack || "").split("\n")) {
    const match = line.match(/^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?\s*$/)
    if (!match) continue
    frames.push({
      platform: "web:javascript",
      filename: match[2],
      function: match[1] || "<anonymous>",
      lineno: Number(match[3]),
      colno: Number(match[4]),
      in_app: true,
    })
  }
  return frames.reverse()
}

export function createPosthog({ apiKey, apiHost, getDistinctId, defaultProps = {} }) {
  const base = apiHost.replace(/\/$/, "")
  const clientContextPromise = getClientContext().catch(() => ({}))
  const exceptionLimiter = createExceptionLimiter({ bucketSize: 10, windowMs: 10_000 })

  async function send(event, properties = {}, timestamp = new Date().toISOString()) {
    const distinctId = await getDistinctId()
    if (!distinctId) return

    const payload = {
      api_key: apiKey,
      distinct_id: distinctId,
      event,
      properties: { ...defaultProps, ...(await clientContextPromise), ...properties },
      timestamp,
    }

    try {
      await fetch(`${base}/i/v0/e/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
    } catch {}
  }

  const capture = async (event, properties) => send(event, properties)

  const captureException = async (error, properties = {}, { handled = true } = {}) => {
    const name = error?.name || "Error"
    const message = error?.message || String(error)
    if (!exceptionLimiter.allow(`${name}::${message}`)) return

    const frames = parseStackFrames(error?.stack)
    return send("$exception", {
      ...properties,
      $exception_level: "error",
      $exception_list: [
        {
          type: name,
          value: message,
          mechanism: { handled: !!handled, synthetic: false },
          stacktrace: frames.length ? { type: "raw", frames } : undefined,
        },
      ],
    })
  }

  return { capture, captureException }
}

let tracker = null
export const setTracker = (trackerFn) => (tracker = trackerFn)

export const track = (event, properties) => {
  if (tracker) tracker(event, properties)
  else if (typeof chrome !== "undefined" && chrome.runtime?.id)
    chrome.runtime.sendMessage({ action: "track", event, properties })
}

const errorInfo = (error) => {
  if (error instanceof Error) return { name: error.name, message: error.message, stack: error.stack || "" }
  if (typeof ErrorEvent !== "undefined" && error instanceof ErrorEvent) return errorInfo(error.error ?? error.message)
  return { name: error?.name || "Error", message: String(error?.message ?? error), stack: error?.stack || "" }
}

export const trackException = (error, properties = {}) => {
  track("$exception", { ...properties, exception: errorInfo(error) })
}
